import { UploadAssetResponse } from 'base/accounts_service';
import { Asset } from 'base/asset';
import { ServiceError, ServiceHttpError } from 'base/service_error';
import { ToastQueue } from 'base/ui/toast';
import { Tooltip } from 'base/ui/tooltip';
import { TextTiny } from 'base/ui/typography';
import React from 'react';
import styles from './ui.css';

export function TwoColumn({
  spacing = 16,
  children,
}: {
  spacing?: 16 | 8;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.twoColumn} style={{ gap: spacing + 'px' }}>
      {children}
    </div>
  );
}

export function DiscountChip({
  percent,
  tooltip,
}: {
  percent: number;
  tooltip?: string;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  return (
    <div className={styles.discountChip} ref={ref}>
      <TextTiny>{percent.toFixed(0)}% OFF</TextTiny>
      {tooltip && <Tooltip label={tooltip} anchorRef={ref} />}
    </div>
  );
}

function useAssetUpload({
  upload,
  remove,
  toastQueue,
  currentAsset,
  assetType,
}: {
  upload: (file: File) => Promise<UploadAssetResponse>;
  remove: () => Promise<{}>;
  toastQueue: ToastQueue;
  currentAsset?: Asset;
  assetType: string;
}) {
  const [asset, setAsset] = React.useState(currentAsset);
  const [loading, setLoading] = React.useState(false);

  const uploadAsset = async (file: File) => {
    try {
      const response = await upload(file);
      if ('asset' in response) {
        setAsset(response.asset);
      } else {
        setAsset(response.avatar);
      }
    } catch (error) {
      if (
        error instanceof ServiceHttpError &&
        error.responseText.includes('Unsupported')
      ) {
        toastQueue.push({
          label: `Format not supported. Upload a ${assetType} file`,
          duration: 10_000,
        });
      } else if (error instanceof ServiceHttpError && error.status === 413) {
        toastQueue.push({
          label: 'File too large. Use a file smaller than 4MB',
          duration: 10_000,
        });
      } else if (
        error instanceof ServiceError &&
        !(error instanceof ServiceHttpError)
      ) {
        toastQueue.push({ label: 'Bad network connection. Please try again' });
      } else {
        toastQueue.push({
          label: 'Hmm something went wrong. Please try again',
        });
        throw error;
      }
    }
  };

  const chooseAsset = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      uploadAsset &&
      event.currentTarget.files &&
      event.currentTarget.files.length === 1
    ) {
      const file = event.currentTarget.files[0];
      setLoading(true);
      uploadAsset(file).finally(() => setLoading(false));
      event.currentTarget.value = '';
    }
  };
  const deleteAsset = async () => {
    setLoading(true);
    try {
      await remove();
      setAsset(undefined);
    } catch (error) {
      toastQueue.push({
        label: 'Hmm something went wrong. Please try again',
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    asset,
    loading,
    chooseAsset,
    deleteAsset,
  };
}

export function useAvatarUpload({
  upload,
  remove,
  toastQueue,
  currentAvatar,
}: {
  upload: (file: File) => Promise<UploadAssetResponse>;
  remove: () => Promise<{}>;
  toastQueue: ToastQueue;
  currentAvatar?: Asset;
}) {
  const hook = useAssetUpload({
    upload,
    remove,
    toastQueue,
    currentAsset: currentAvatar,
    assetType: 'PNG or JPG',
  });

  return {
    loading: hook.loading,
    avatar: hook.asset,
    chooseAvatar: hook.chooseAsset,
    deleteAvatar: hook.deleteAsset,
  };
}

export function useLogoUpload({
  upload,
  remove,
  toastQueue,
  currentLogo,
}: {
  upload: (file: File) => Promise<UploadAssetResponse>;
  remove: () => Promise<{}>;
  toastQueue: ToastQueue;
  currentLogo?: Asset;
}) {
  const hook = useAssetUpload({
    upload,
    remove,
    toastQueue,
    currentAsset: currentLogo,
    assetType: 'SVG or PNG',
  });

  return {
    loading: hook.loading,
    avatar: hook.asset,
    chooseLogo: hook.chooseAsset,
    deleteLogo: hook.deleteAsset,
  };
}
